import type { Screenshot } from 'types';
import { useState, useRef } from 'react';
import { Trash, Pencil, Check, X, Loader2, Download } from 'lucide-react';
import { cn } from 'helpers/cn';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ConfirmDialog } from '@/components/Dialogs';
import { useScreenshotImage } from '@/state/experiments/useScreenshotImage';
import { FOCUS_RING } from '../../../../ConsolePanel';
import { ScreenshotDialog } from './ScreenshotDialog';

// border-input, not border-line: these carry a panel fill but sit on top of the
// screenshot itself, so the outline is what separates them from the image.
const ICON_BUTTON_CLASS = 'border-input bg-panel text-dim hover:text-foreground size-7 rounded-lg border';

type ScreenshotItemProps = {
  screenshot: Screenshot;
  onSave: () => void;
  onDelete: (screenshotId: number) => void;
  editing: number | null;
  setEditing: (id: number | null) => void;
  setCommentValue: (value: string) => void;
};

export const ScreenshotItem = (props: ScreenshotItemProps) => {
  const { screenshot, onSave, onDelete, editing, setEditing, setCommentValue } = props;
  const [isOpen, setIsOpen] = useState(false);
  const [wantsToDelete, setWantsToDelete] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const isEditing = editing === screenshot.id;
  const screenshotRef = useRef<HTMLDivElement>(null);

  // Load image from file server (cached via React Query)
  const { imageSrc } = useScreenshotImage(screenshot.url);

  const onDeleteConfirm = async () => {
    await onDelete(screenshot.id);
    setWantsToDelete(false);
  };

  const onStartEdit = () => {
    setCommentValue(screenshot.comment || '');
    setEditing(screenshot.id);
  };

  const onDownload = () => {
    if (!imageSrc) return;
    const link = document.createElement('a');
    link.href = imageSrc;
    link.download = `screenshot-${screenshot.id}.png`;
    link.click();
  };

  return (
    <div ref={screenshotRef} className="border-line bg-panel2 rounded-card group relative overflow-hidden border">
      <div className="bg-field relative flex aspect-video items-center justify-center overflow-hidden">
        {!imageSrc && (
          <div className="bg-field absolute inset-0 flex items-center justify-center">
            <Loader2 className="text-dim size-5 animate-spin" strokeWidth={1.5} />
          </div>
        )}
        {imageSrc && (
          // A real button, not `<img onClick>`: the full-size view was mouse-only,
          // with cursor-pointer as its only hint and no way to open it from the
          // keyboard.
          <button
            type="button"
            onClick={() => setIsOpen(true)}
            title="Open full size"
            className={cn('flex size-full items-center justify-center', FOCUS_RING)}
          >
            <span className="sr-only">Open screenshot full size</span>
            <img
              src={imageSrc}
              alt={screenshot.comment || 'Screenshot'}
              onLoad={() => setImageLoaded(true)}
              className={cn(
                'max-h-full max-w-full object-contain transition-transform group-hover:scale-105',
                imageLoaded ? 'opacity-100' : 'opacity-0',
              )}
            />
          </button>
        )}
      </div>
      <div className="min-h-[52px] p-3">
        {isEditing ? (
          <Textarea
            onChange={(ev) => setCommentValue(ev.target.value)}
            defaultValue={screenshot.comment || ''}
            placeholder="Add a comment..."
            className="border-input bg-field text-foreground resize-none rounded-lg text-meta"
            rows={2}
          />
        ) : (
          <p className="supporting line-clamp-2" title={screenshot.comment || undefined}>
            {screenshot.comment || 'No comment'}
          </p>
        )}
      </div>

      {/*
        Always in the DOM, revealed by CSS.
        `group-focus-within` is what makes these reachable at all — tabbing into a
        transparent button turns the set opaque — and `max-md:opacity-100` covers
        touch, where there is no hover to reveal anything. Mid-edit the set stays
        visible regardless, because Save and Cancel are the only way out.
      */}
      <div
        className={cn(
          'absolute top-2 right-2 flex gap-1 transition-opacity duration-150',
          'group-hover:opacity-100 group-focus-within:opacity-100 max-md:opacity-100',
          isEditing ? 'opacity-100' : 'opacity-0',
        )}
      >
        {isEditing ? (
          <>
            <Button
              size="icon"
              variant="secondary"
              aria-label="Cancel editing comment"
              title="Cancel editing comment"
              className={ICON_BUTTON_CLASS}
              onClick={() => setEditing(null)}
            >
              <X className="size-3.5" strokeWidth={1.5} />
            </Button>
            <Button
              size="icon"
              aria-label="Save comment"
              title="Save comment"
              className={cn(ICON_BUTTON_CLASS, 'bg-cta hover:bg-ctahover text-white')}
              onClick={onSave}
            >
              <Check className="size-3.5" strokeWidth={1.5} />
            </Button>
          </>
        ) : (
          <>
            <Button
              size="icon"
              variant="secondary"
              aria-label="Edit comment"
              title="Edit comment"
              className={ICON_BUTTON_CLASS}
              onClick={onStartEdit}
            >
              <Pencil className="size-3.5" strokeWidth={1.5} />
            </Button>
            <Button
              size="icon"
              variant="secondary"
              aria-label="Download screenshot"
              title="Download screenshot"
              className={ICON_BUTTON_CLASS}
              onClick={onDownload}
            >
              <Download className="size-3.5" strokeWidth={1.5} />
            </Button>
          </>
        )}
        <Button
          size="icon"
          variant="destructive"
          aria-label="Delete screenshot"
          title="Delete screenshot"
          className={cn(ICON_BUTTON_CLASS, 'bg-danger-bg border-danger-line text-danger hover:bg-danger-bg border')}
          onClick={() => setWantsToDelete(true)}
        >
          <Trash className="size-3.5" strokeWidth={1.5} />
        </Button>
      </div>

      {isOpen && imageSrc && (
        <ScreenshotDialog screenshot={screenshot} imageSrc={imageSrc} onClose={() => setIsOpen(false)} />
      )}

      <ConfirmDialog
        open={wantsToDelete}
        onCancel={() => setWantsToDelete(false)}
        title="Delete Screenshot"
        description="Are you sure you want to delete this screenshot? This action cannot be undone."
        confirmLabel="Delete"
        confirmVariant="destructive"
        onConfirm={onDeleteConfirm}
      >
        {null}
      </ConfirmDialog>
    </div>
  );
};
